/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Generic ModuleTabs — renders a horizontal tab bar filtered by the current
 * user's tabAccess permissions. Also provides a route-level <TabGate /> that
 * shows a "Permission Denied" view when a user tries to navigate directly to
 * a tab they don't have access to.
 *
 * Both components are 100% registry-driven — they read from PLATFORM_MODULES
 * and never hard-code module or tab IDs.
 */

import React, { useEffect } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { User, UserRole } from '../../domain/entities';
import { canAccessTab } from '../../core/security';
import { PLATFORM_MODULES } from '../../core/appRegistry';
import { Shield } from 'lucide-react';
import { cn } from '../../ui/utils';
import { useToast } from '../../ui/Toast';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ModuleTabItem {
  /** Tab key — must match the tab id in appRegistry. */
  key: string;
  /** Display label. */
  label: string;
  /** Lucide icon element. */
  icon?: React.ReactNode;
  /** Optional badge count (e.g. pending items). */
  badge?: number;
  /** Route path this tab navigates to. */
  to: string;
  /** If true, match pathname exactly (===) instead of startsWith. */
  exact?: boolean;
}

// ─── ModuleTabs ───────────────────────────────────────────────────────────────

interface ModuleTabsProps {
  /** The module id (e.g. 'rider-manager'). */
  moduleId: string;
  /** Complete list of tabs the module *could* render. */
  tabs: ModuleTabItem[];
  /** The currently logged-in user. */
  user: User | null;
  /** Optional content rendered on the right side of the tab bar. */
  headerRight?: React.ReactNode;
}

/**
 * Renders a filtered horizontal tab bar. Tabs are hidden when the user does
 * not have `tabAccess` for them. SUPER_ADMIN and ADMIN bypass; DEPARTMENT_HEAD,
 * MANAGER and EMPLOYEE are gated by explicit tabAccess arrays.
 */
export function ModuleTabs({ moduleId, tabs, user, headerRight }: ModuleTabsProps) {
  const location = useLocation();

  // Filter tabs based on user's tabAccess
  const visibleTabs = tabs.filter(tab => canAccessTab(user, moduleId, tab.key));

  if (visibleTabs.length === 0) return null;

  return (
    <div className="flex items-center justify-between border-b border-slate-200">
      <div className="flex items-center">
        {visibleTabs.map(tab => {
          const active = tab.exact
            ? location.pathname === tab.to
            : location.pathname.startsWith(tab.to);
          return (
            <Link
              key={tab.key}
              to={tab.to}
              className={cn(
                'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-all duration-200',
                active
                  ? 'border-slate-900 text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300',
              )}
            >
              {tab.icon}
              {tab.label}
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className="ml-1 min-w-[18px] h-[18px] px-1 bg-emerald-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {tab.badge}
                </span>
              )}
            </Link>
          );
        })}
      </div>
      {headerRight && <div className="flex items-center gap-3 pb-1">{headerRight}</div>}
    </div>
  );
}

// ─── TabGate ──────────────────────────────────────────────────────────────────

interface TabGateProps {
  /** The module id (e.g. 'rider-manager'). */
  moduleId: string;
  /** The tab id being accessed (e.g. 'database'). */
  tabId: string;
  /** The currently logged-in user. */
  user: User | null;
  /** Content to render when access is granted. */
  children: React.ReactNode;
}

/**
 * Route-level security gate for individual tabs. If the user doesn't have
 * access to this tab, renders a Permission Denied view instead of the
 * children. Prevents users from typing a URL directly to bypass tab hiding.
 */
export function TabGate({ moduleId, tabId, user, children }: TabGateProps) {
  const toast = useToast();
  const denied = !canAccessTab(user, moduleId, tabId);

  useEffect(() => {
    if (denied) {
      toast.error(
        'Access Denied',
        'You do not have permission to manage this section. Contact Super Admin.'
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [denied]);

  if (!denied) {
    return <>{children}</>;
  }

  return (
    <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
      <div className="w-16 h-16 bg-zinc-900 rounded-2xl flex items-center justify-center text-white">
        <Shield size={28} />
      </div>
      <div>
        <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-2">403 — Tab Access Restricted</p>
        <h2 className="text-2xl font-bold tracking-tight text-zinc-900">Permission Required</h2>
        <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">
          You do not have access to this section. Contact your administrator to request access.
        </p>
      </div>
    </div>
  );
}
